import React, { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { useTheme, DEFAULT_THEME } from "@/context/ThemeContext";
import { toast } from "sonner";
import { Save, RotateCcw, Palette, Sidebar, Eye } from "lucide-react";

/**
 * ThemeSettings
 * -------------
 * Tenant-scoped UI to pick the brand colors (primary / sidebar / background /
 * accent / text) and choose the sidebar mode. Renders a live preview card on
 * the right so the operator can SEE the result before hitting Save.
 *
 * The actual CSS variables are flipped at PUT-time by the ThemeContext, so
 * the entire app (including the sidebar this user is sitting in) re-themes
 * instantly without a page reload.
 */
const SWATCHES = [
  { primary: "#E84672", sidebar: "#E84672", bg: "#7DDDDD", accent: "#2DD4D4", text: "#1E2A4A", label_ar: "وردي / تركوازي", label_en: "Pink / Turquoise" },
  { primary: "#7A1F3D", sidebar: "#7A1F3D", bg: "#F8FAFC", accent: "#D4A23C", text: "#1E2A4A", label_ar: "عنابي كلاسيكي",   label_en: "Classic Burgundy" },
  { primary: "#0F766E", sidebar: "#0F766E", bg: "#F0FDFA", accent: "#F59E0B", text: "#134E4A", label_ar: "أخضر فيروزي",     label_en: "Emerald Spa" },
  { primary: "#7C3AED", sidebar: "#7C3AED", bg: "#FAF5FF", accent: "#F472B6", text: "#1E1B4B", label_ar: "بنفسجي ملكي",     label_en: "Royal Purple" },
  { primary: "#0EA5E9", sidebar: "#0F172A", bg: "#F1F5F9", accent: "#38BDF8", text: "#0F172A", label_ar: "أزرق غامق",       label_en: "Midnight Blue" },
  { primary: "#EA580C", sidebar: "#1F2937", bg: "#FFF7ED", accent: "#FBBF24", text: "#1F2937", label_ar: "برتقالي فحمي",   label_en: "Charcoal Sunset" },
];

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const Field = ({ label, value, onChange, testId }) => {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);
  const commit = (v) => {
    setText(v);
    if (HEX_RE.test(v)) onChange(v);
  };
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-[var(--text)]">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testId}-picker`}
          className="w-12 h-10 rounded-md border border-[var(--border)] cursor-pointer bg-white"
        />
        <input
          type="text"
          value={text}
          onChange={(e) => commit(e.target.value)}
          data-testid={`${testId}-hex`}
          className="input-dark flex-1 font-mono uppercase tracking-wider text-sm"
          placeholder="#RRGGBB"
          maxLength={7}
        />
      </div>
    </div>
  );
};

export default function ThemeSettings() {
  const { lang } = useLanguage();
  const { theme, saveTheme, resetTheme } = useTheme();
  const isAr = lang === "ar";
  const [draft, setDraft] = useState(theme);
  const [saving, setSaving] = useState(false);

  // Keep draft in sync if the live theme changes (e.g. after reset).
  useEffect(() => { setDraft(theme); }, [theme]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(theme);

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const applySwatch = (s) => setDraft((d) => ({ ...d, ...s }));

  const onSave = async () => {
    setSaving(true);
    try {
      await saveTheme(draft);
      toast.success(isAr ? "تم حفظ الثيم وتطبيقه" : "Theme saved and applied");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? "تعذر الحفظ" : "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const onReset = async () => {
    setSaving(true);
    try {
      const r = await resetTheme();
      setDraft(r);
      toast.success(isAr ? "تم استرجاع الثيم الافتراضي" : "Theme reset to defaults");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? "تعذر الاسترجاع" : "Reset failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="theme-settings-panel">
      {/* ─── Color pickers + sidebar mode ─── */}
      <div className="lg:col-span-2 card-surface p-6 space-y-6">
        <div>
          <h3 className="font-heading text-xl text-[var(--text)] flex items-center gap-2">
            <Palette size={18} className="text-[var(--gold)]" />
            {isAr ? "ألوان النظام" : "System Colors"}
          </h3>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            {isAr
              ? "اختر الألوان عبر منتقي اللون أو اكتب كود اللون (HEX) مباشرة. كل صالون يحفظ ثيمه الخاص."
              : "Pick colors via the swatch or paste a HEX code. Each salon keeps its own theme."}
          </p>
        </div>

        {/* Preset swatches */}
        <div>
          <div className="text-xs font-semibold text-[var(--text)] mb-2">
            {isAr ? "ثيمات جاهزة" : "Preset themes"}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="theme-presets">
            {SWATCHES.map((s, i) => (
              <button
                key={i}
                onClick={() => applySwatch(s)}
                data-testid={`theme-preset-${i}`}
                className="group flex items-center gap-2 p-2.5 rounded-lg border border-[var(--border)] hover:border-[var(--gold)] hover:shadow-md transition text-start bg-white"
              >
                <div className="flex -space-x-1 rtl:space-x-reverse">
                  <span className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ background: s.primary }} />
                  <span className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ background: s.bg }} />
                  <span className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ background: s.accent }} />
                </div>
                <span className="text-xs font-medium text-[var(--text)]">{isAr ? s.label_ar : s.label_en}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="h-px bg-[var(--border-subtle)]" />

        {/* Individual color fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={isAr ? "اللون الرئيسي" : "Primary color"}        value={draft.primary} onChange={(v) => setField("primary", v)} testId="theme-primary" />
          <Field label={isAr ? "لون القائمة الجانبية" : "Sidebar color"}  value={draft.sidebar} onChange={(v) => setField("sidebar", v)} testId="theme-sidebar" />
          <Field label={isAr ? "لون الخلفية" : "Background color"}        value={draft.bg}      onChange={(v) => setField("bg", v)}      testId="theme-bg" />
          <Field label={isAr ? "لون التمييز" : "Accent color"}             value={draft.accent}  onChange={(v) => setField("accent", v)}  testId="theme-accent" />
          <Field label={isAr ? "لون النص" : "Text color"}                   value={draft.text}    onChange={(v) => setField("text", v)}    testId="theme-text" />
        </div>

        <div className="h-px bg-[var(--border-subtle)]" />

        {/* Sidebar mode toggle */}
        <div>
          <h3 className="font-heading text-base text-[var(--text)] flex items-center gap-2 mb-3">
            <Sidebar size={16} className="text-[var(--gold)]" />
            {isAr ? "وضع القائمة الجانبية" : "Sidebar mode"}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="sidebar-mode-group">
            {[
              { id: "fixed",     label_ar: "ثابتة (دائماً مفتوحة)",    label_en: "Fixed (always open)",      desc_ar: "تظل القائمة مفتوحة على عرضها الكامل دائماً.", desc_en: "Always 256px wide with labels visible." },
              { id: "auto-hide", label_ar: "متحركة (تختفي تلقائياً)", label_en: "Auto-hide (slide on hover)", desc_ar: "تنكمش إلى أيقونات وتنفتح عند المرور بالماوس.", desc_en: "Collapses to icons; expands on hover." },
            ].map((m) => {
              const active = draft.sidebar_mode === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => setField("sidebar_mode", m.id)}
                  data-testid={`sidebar-mode-${m.id}`}
                  className={`text-start p-4 rounded-xl border-2 transition ${active ? "border-[var(--gold)] bg-[var(--gold-tint)]" : "border-[var(--border)] hover:border-[var(--border-strong)] bg-white"}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-sm text-[var(--text)]">{isAr ? m.label_ar : m.label_en}</span>
                    {active && <span className="w-2 h-2 rounded-full bg-[var(--gold)]" />}
                  </div>
                  <div className="text-xs text-[var(--text-muted)] leading-relaxed">{isAr ? m.desc_ar : m.desc_en}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={onSave}
            disabled={!dirty || saving}
            data-testid="theme-save-btn"
            className="btn-gold px-5 py-2.5 rounded-md flex items-center gap-2 disabled:opacity-50"
          >
            <Save size={15} />
            {isAr ? "حفظ وتطبيق" : "Save & apply"}
          </button>
          <button
            onClick={onReset}
            disabled={saving}
            data-testid="theme-reset-btn"
            className="btn-secondary px-5 py-2.5 rounded-md flex items-center gap-2"
          >
            <RotateCcw size={14} />
            {isAr ? "استرجاع الافتراضي" : "Reset to default"}
          </button>
          {dirty && (
            <span className="text-xs text-amber-700 font-medium">
              {isAr ? "تغييرات غير محفوظة" : "Unsaved changes"}
            </span>
          )}
        </div>
      </div>

      {/* ─── Live preview ─── */}
      <div className="card-surface p-5" data-testid="theme-preview">
        <h3 className="font-heading text-base text-[var(--text)] flex items-center gap-2 mb-4">
          <Eye size={16} className="text-[var(--gold)]" />
          {isAr ? "معاينة مباشرة" : "Live preview"}
        </h3>
        <div className="space-y-3">
          {/* Mini app preview */}
          <div className="rounded-xl overflow-hidden border border-[var(--border)] shadow-sm">
            {/* Topbar */}
            <div className="h-10 flex items-center justify-between px-3" style={{ background: draft.sidebar }}>
              <span className="text-white text-[10px] font-semibold">iSaloon</span>
              <span className="text-white/80 text-[9px]">user@salon</span>
            </div>
            <div className="flex" style={{ background: draft.bg }}>
              {/* Sidebar */}
              <div className="w-20 py-3 px-2 space-y-1" style={{ background: draft.sidebar }}>
                <div className="h-6 rounded-md flex items-center px-2 bg-white text-[9px] font-semibold" style={{ color: draft.primary }}>
                  Active
                </div>
                <div className="h-6 rounded-md px-2 flex items-center text-white/80 text-[9px]">Item</div>
                <div className="h-6 rounded-md px-2 flex items-center text-white/80 text-[9px]">Item</div>
              </div>
              {/* Content */}
              <div className="flex-1 p-3 space-y-2">
                <div className="bg-white rounded-lg p-2.5 shadow-sm">
                  <div className="text-[10px] font-bold" style={{ color: draft.text }}>Card title</div>
                  <div className="text-[9px] mt-1" style={{ color: draft.text, opacity: 0.6 }}>Body text uses --text</div>
                </div>
                <div className="flex gap-2">
                  <button className="text-[9px] text-white px-2 py-1 rounded font-semibold" style={{ background: draft.primary }}>
                    Primary
                  </button>
                  <span className="text-[9px] px-2 py-1 rounded font-semibold" style={{ background: draft.accent, color: "#0F172A" }}>
                    Accent
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Color chips */}
          <div className="grid grid-cols-5 gap-1.5 pt-1">
            {[
              { key: "primary", c: draft.primary },
              { key: "sidebar", c: draft.sidebar },
              { key: "bg",      c: draft.bg },
              { key: "accent",  c: draft.accent },
              { key: "text",    c: draft.text },
            ].map((x) => (
              <div key={x.key} className="text-center">
                <div className="w-full aspect-square rounded-md border border-[var(--border)] shadow-sm" style={{ background: x.c }} />
                <div className="text-[8px] text-[var(--text-muted)] mt-1 uppercase tracking-wide">{x.key}</div>
              </div>
            ))}
          </div>

          <div className="text-[10px] text-[var(--text-muted)] leading-relaxed pt-1">
            {isAr
              ? "ملاحظة: يتم تطبيق الثيم على كل المستخدمين داخل صالونك. صفحات تسجيل الدخول العامة تبقى بالألوان الافتراضية."
              : "Note: The theme applies to every user in your salon. Public login pages use the platform defaults."}
          </div>
        </div>
      </div>
    </div>
  );
}
